/**
 * <resource-list-nav>
 *
 * Header-row previous/next controls for a resource-list mobile carousel. The
 * slideshow's own arrows live inside `.resource-list__carousel`, which is a
 * containment context (container-type), so they can't be lifted out into the
 * section header via CSS. Instead we render a small button pair in the header
 * row — across from the heading — and forward clicks to the sibling
 * slideshow-component, mirroring its disabled state.
 */
class ResourceListNav extends HTMLElement {
  connectedCallback() {
    this.previousButton = this.querySelector('[data-nav="previous"]');
    this.nextButton = this.querySelector('[data-nav="next"]');

    this.previousButton?.addEventListener('click', () => this.#go('previous'));
    this.nextButton?.addEventListener('click', () => this.#go('next'));

    this.root = this.closest('.section-resource-list, product-recommendations, .block-resource-list') || document;
    this.root.addEventListener('slideshow:select', this.update);

    // Slideshow may still be upgrading/initialising when we connect.
    requestAnimationFrame(() => requestAnimationFrame(this.update));
  }

  disconnectedCallback() {
    this.root?.removeEventListener('slideshow:select', this.update);
  }

  /**
   * Resolve the slideshow this nav controls. Prefer the mobile-only carousel
   * (rendered for `carousel_on_mobile` grids); fall back to any slideshow in
   * the block (full-carousel layout shows the same one on every breakpoint).
   * @returns {(HTMLElement & { next?: Function, previous?: Function, current?: number, nextIndex?: number, slides?: unknown[] }) | null}
   */
  get slideshow() {
    if (!this.root) return null;
    return (
      this.root.querySelector('.resource-list.hidden--desktop slideshow-component') ||
      this.root.querySelector('slideshow-component')
    );
  }

  /** @param {'previous' | 'next'} direction */
  #go(direction) {
    const slideshow = this.slideshow;
    if (typeof slideshow?.[direction] !== 'function') return;
    slideshow[direction]();
    requestAnimationFrame(this.update);
  }

  update = () => {
    const slideshow = this.slideshow;
    if (!slideshow) {
      // No carousel yet (e.g. recommendations still loading) — read as inactive.
      if (this.previousButton) this.previousButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
      return;
    }

    const index = typeof slideshow.current === 'number' ? slideshow.current : 0;
    const count = Array.isArray(slideshow.slides) ? slideshow.slides.length : 0;
    const nextIndex = typeof slideshow.nextIndex === 'number' ? slideshow.nextIndex : index + 1;

    if (this.previousButton) this.previousButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = count > 0 ? nextIndex >= count : false;
  };
}

if (!customElements.get('resource-list-nav')) {
  customElements.define('resource-list-nav', ResourceListNav);
}
